import { hexToSpaceSeparatedRgb } from './palette';
import type { DesignTheme, FlatThemeRecord, PaletteStep, PrimitiveFamily } from './types';
import { PALETTE_STEPS } from './types';

const PRIMITIVE_FAMILIES: PrimitiveFamily[] = [
  'primary',
  'secondary',
  'neutral',
  'error',
  'warning',
  'success',
  'info',
];

function emitPrimitives(theme: DesignTheme): string[] {
  const lines: string[] = [];
  for (const family of PRIMITIVE_FAMILIES) {
    const { light, dark } = theme.primitives[family];
    for (const step of PALETTE_STEPS) {
      lines.push(`  --palette-${family}-light-${step}: ${light[step]};`);
      lines.push(`  --palette-${family}-dark-${step}: ${dark[step]};`);
    }
  }
  return lines;
}

/** Current-theme-relative steps (switch under `.dark`). */
function emitPaletteAliases(mode: 'light' | 'dark'): string[] {
  const lines: string[] = [];
  const col = mode === 'light' ? 'light' : 'dark';
  for (const family of PRIMITIVE_FAMILIES) {
    for (const step of PALETTE_STEPS) {
      lines.push(`  --palette-${family}-${step}: var(--palette-${family}-${col}-${step});`);
    }
  }
  return lines;
}

function emitState(theme: DesignTheme, mode: 'light' | 'dark'): string[] {
  const s = theme.state[mode];
  return [
    `  --state-hover: ${s.hover};`,
    `  --state-pressed: ${s.pressed};`,
    `  --state-focus: ${s.focus};`,
    `  --state-focus-border: ${s.focusBorder};`,
    `  --state-disabled: ${s.disabled};`,
    `  --state-selected: ${s.selected};`,
    `  --state-hover-overlay: ${s.hoverOverlay};`,
    `  --state-pressed-overlay: ${s.pressedOverlay};`,
    `  --state-disabled-text: ${s.disabledText};`,
    `  --state-disabled-bg: ${s.disabledBg};`,
    `  --state-focus-ring: ${s.focusRing};`,
  ];
}

function emitSemantic(theme: DesignTheme, mode: 'light' | 'dark'): string[] {
  const s = theme.semantic[mode];
  /** Aliases for marketing sections / chips (switch automatically under `.dark`). */
  const surfaceElevated = s.bg.secondary;
  const surfaceMuted = s.bg.tertiary;
  return [
    `  --semantic-bg-primary: ${s.bg.primary};`,
    `  --semantic-bg-secondary: ${s.bg.secondary};`,
    `  --semantic-bg-tertiary: ${s.bg.tertiary};`,
    `  --semantic-surface-elevated: ${surfaceElevated};`,
    `  --semantic-surface-muted: ${surfaceMuted};`,
    `  --semantic-bg-inverse: ${s.bg.inverse};`,
    `  --semantic-text-primary: ${s.text.primary};`,
    `  --semantic-text-secondary: ${s.text.secondary};`,
    `  --semantic-text-disabled: ${s.text.disabled};`,
    `  --semantic-text-inverse: ${s.text.inverse};`,
    `  --semantic-border-default: ${s.border.default};`,
    `  --semantic-border-subtle: ${s.border.subtle};`,
    `  --semantic-border-strong: ${s.border.strong};`,
    `  --semantic-icon-primary: ${s.icon.primary};`,
  ];
}

function emitGlass(theme: DesignTheme, mode: 'light' | 'dark'): string[] {
  const g = theme.glass[mode];
  return [
    `  --glass-fill: ${g.fill};`,
    `  --glass-fill-elevated: ${g.fillElevated};`,
    `  --glass-stroke: ${g.stroke};`,
    `  --glass-highlight: ${g.highlight};`,
    `  --glass-shade: ${g.shade};`,
    `  --glass-blur: ${g.blur};`,
    `  --glass-saturate: ${g.saturate};`,
  ];
}

/** Radius, motion, control sizing — shared across light/dark (`defaultTheme.ui`). */
function emitUi(ui: DesignTheme['ui']): string[] {
  const { radius: r, motion: m, control: c } = ui;
  return [
    `  --ui-radius-xs: ${r.xs};`,
    `  --ui-radius-sm: ${r.sm};`,
    `  --ui-radius-md: ${r.md};`,
    `  --ui-radius-lg: ${r.lg};`,
    `  --ui-radius-xl: ${r.xl};`,
    `  --ui-radius-full: ${r.full};`,
    `  --ui-motion-duration-xs: ${m.durationXs};`,
    `  --ui-motion-duration-sm: ${m.durationSm};`,
    `  --ui-motion-duration-md: ${m.durationMd};`,
    `  --ui-motion-duration-lg: ${m.durationLg};`,
    `  --ui-motion-duration-xl: ${m.durationXl};`,
    `  --ui-motion-easing-standard: ${m.easingStandard};`,
    `  --ui-motion-easing-emphasized: ${m.easingEmphasized};`,
    `  --ui-motion-easing-decelerate: ${m.easingDecelerate};`,
    `  --ui-control-padding-x: ${c.paddingX};`,
    `  --ui-control-padding-y: ${c.paddingY};`,
    `  --ui-control-min-height-touch: ${c.minHeightTouch};`,
    `  --ui-control-border-width: ${c.borderWidth};`,
    `  --ui-control-font-size: ${c.fontSize};`,
    `  --ui-label-font-size: ${c.labelFontSize};`,
    `  --ui-label-line-height: ${c.labelLineHeight};`,
    `  --ui-label-font-weight: ${c.labelFontWeight};`,
  ];
}

/** Viewport tiers → `--bp-*` (shared with Tailwind `--breakpoint-*` in `variables.css`). */
function emitBreakpoints(bp: DesignTheme['ui']['breakpoints']): string[] {
  return [
    `  --bp-watch-max: ${bp.watchMax};`,
    `  --bp-xs: ${bp.xs};`,
    `  --bp-sm: ${bp.sm};`,
    `  --bp-md: ${bp.md};`,
    `  --bp-lg: ${bp.lg};`,
    `  --bp-xl: ${bp.xl};`,
    `  --bp-2xl: ${bp.twoXl};`,
    `  --bp-3xl: ${bp.threeXl};`,
    `  --bp-4xl: ${bp.fourXl};`,
  ];
}

function opacityMix(baseVar: string, pct: number): string {
  return `color-mix(in srgb, ${baseVar} ${pct}%, transparent)`;
}

function emitOpacityRamps(theme: DesignTheme): string[] {
  const { primaryStep, secondaryStep, neutralDarkStep, neutralLightStep } = theme.opacityBases;
  const pBase = `var(--palette-primary-light-${primaryStep})`;
  const sBase = `var(--palette-secondary-light-${secondaryStep})`;
  const n900 = `var(--palette-neutral-light-${neutralDarkStep})`;
  const n50 = `var(--palette-neutral-light-${neutralLightStep})`;
  const lines: string[] = [];
  for (const pct of [100, 80, 60, 40, 20]) {
    lines.push(`  --opacity-primary-${pct}: ${opacityMix(pBase, pct)};`);
    lines.push(`  --opacity-secondary-${pct}: ${opacityMix(sBase, pct)};`);
    lines.push(`  --opacity-neutral-900-${pct}: ${opacityMix(n900, pct)};`);
  }
  lines.push(`  --opacity-neutral-50-100: ${n50};`);
  for (const pct of [100, 80, 60, 40, 20]) {
    lines.push(`  --opacity-white-${pct}: ${opacityMix('white', pct)};`);
    lines.push(`  --opacity-black-${pct}: ${opacityMix('black', pct)};`);
  }
  return lines;
}

/** Legacy + Tailwind bridge tokens used across existing CSS. */
function emitLegacyBridge(): string[] {
  return [
    '  --color-transparent: transparent;',
    '  --color-white: #ffffff;',
    '  --color-dark: var(--palette-neutral-100);',
    '  --color-bg: var(--semantic-bg-primary);',
    '  --color-text: var(--semantic-text-primary);',
    '  --color-muted: var(--semantic-bg-tertiary);',
    '  --color-light: var(--semantic-bg-tertiary);',
    '  --color-gray: var(--palette-neutral-70);',
    '  --color-primary: var(--palette-primary-50);',
    '  --color-secondary: var(--palette-secondary-50);',
    '  --color-success: var(--palette-success-50);',
    '  --color-danger: var(--palette-error-50);',
    '  --color-warning: var(--palette-warning-50);',
    '  --color-info: var(--palette-info-50);',
    '  --color-blue: var(--palette-primary-60);',
    '  --color-purple: var(--palette-secondary-60);',
    '  --color-smoke: var(--semantic-border-subtle);',
    '  --color-teal: var(--palette-info-50);',
    '  --color-cyan: var(--palette-info-40);',
    '  --color-indigo: var(--palette-secondary-80);',
    '  --color-primary-dark: var(--palette-primary-dark-50);',
    '  --color-secondary-dark: var(--palette-secondary-dark-50);',
    '  --color-bg-dark: var(--semantic-bg-inverse);',
    '  --color-text-dark: var(--semantic-text-primary);',
    '  --color-white-transparent: var(--opacity-white-50);',
    '  --color-black-transparent: var(--opacity-black-50);',
    '  --g1: var(--color-primary);',
    '  --g2: var(--color-secondary);',
    '  --g3: var(--color-danger);',
    '  --g4: var(--color-warning);',
  ];
}

function emitThemeRgbVars(theme: DesignTheme, mode: 'light' | 'dark'): string[] {
  const pStep = theme.opacityBases.primaryStep;
  const sStep = theme.opacityBases.secondaryStep;
  const primaryHex =
    mode === 'light'
      ? theme.primitives.primary.light[pStep]
      : theme.primitives.primary.dark[pStep];
  const secondaryHex =
    mode === 'light'
      ? theme.primitives.secondary.light[sStep]
      : theme.primitives.secondary.dark[sStep];
  const sem = theme.semantic[mode];
  const neutralGray =
    mode === 'light' ? theme.primitives.neutral.light[70] : theme.primitives.neutral.dark[70];
  return [
    `  --color-primary-rgb: ${hexToSpaceSeparatedRgb(primaryHex)};`,
    `  --color-secondary-rgb: ${hexToSpaceSeparatedRgb(secondaryHex)};`,
    `  --background-rgb: ${hexToSpaceSeparatedRgb(sem.bg.primary)};`,
    `  --foreground-rgb: ${hexToSpaceSeparatedRgb(sem.text.primary)};`,
    `  --color-gray-rgb: ${hexToSpaceSeparatedRgb(neutralGray)};`,
  ];
}

/**
 * Full stylesheet for design tokens. Inject once (SSR) and replace innerHTML when the backend sends an updated theme.
 */
export function themeToCssString(theme: DesignTheme): string {
  const root: string[] = [
    ':root {',
    ...emitPrimitives(theme),
    ...emitUi(theme.ui),
    ...emitBreakpoints(theme.ui.breakpoints),
    `  --color-primary-dark-rgb: ${hexToSpaceSeparatedRgb(theme.primitives.primary.dark[theme.opacityBases.primaryStep])};`,
    `  --color-secondary-dark-rgb: ${hexToSpaceSeparatedRgb(theme.primitives.secondary.dark[theme.opacityBases.secondaryStep])};`,
    ...emitPaletteAliases('light'),
    ...emitState(theme, 'light'),
    ...emitSemantic(theme, 'light'),
    ...emitGlass(theme, 'light'),
    ...emitOpacityRamps(theme),
    ...emitLegacyBridge(),
    ...emitThemeRgbVars(theme, 'light'),
    '}',
    '.dark {',
    ...emitPaletteAliases('dark'),
    ...emitState(theme, 'dark'),
    ...emitSemantic(theme, 'dark'),
    ...emitGlass(theme, 'dark'),
    ...emitThemeRgbVars(theme, 'dark'),
    '}',
  ];
  return root.join('\n');
}

export const DESIGN_TOKENS_STYLE_ID = 'design-tokens';

export function flattenTheme(theme: DesignTheme): FlatThemeRecord {
  const out: FlatThemeRecord = {};

  for (const family of PRIMITIVE_FAMILIES) {
    for (const step of PALETTE_STEPS) {
      out[`primitives.${family}.light.${step}`] = theme.primitives[family].light[step];
      out[`primitives.${family}.dark.${step}`] = theme.primitives[family].dark[step];
    }
  }

  const sk = [
    'hover',
    'pressed',
    'focus',
    'focusBorder',
    'disabled',
    'selected',
    'hoverOverlay',
    'pressedOverlay',
    'disabledText',
    'disabledBg',
    'focusRing',
  ] as const;
  for (const k of sk) {
    out[`state.light.${k}`] = theme.state.light[k];
    out[`state.dark.${k}`] = theme.state.dark[k];
  }

  const semKeys = ['bg', 'text', 'border', 'icon'] as const;
  for (const group of semKeys) {
    if (group === 'icon') {
      out['semantic.light.icon.primary'] = theme.semantic.light.icon.primary;
      out['semantic.dark.icon.primary'] = theme.semantic.dark.icon.primary;
    } else if (group === 'bg') {
      for (const k of ['primary', 'secondary', 'tertiary', 'inverse'] as const) {
        out[`semantic.light.bg.${k}`] = theme.semantic.light.bg[k];
        out[`semantic.dark.bg.${k}`] = theme.semantic.dark.bg[k];
      }
    } else if (group === 'text') {
      for (const k of ['primary', 'secondary', 'disabled', 'inverse'] as const) {
        out[`semantic.light.text.${k}`] = theme.semantic.light.text[k];
        out[`semantic.dark.text.${k}`] = theme.semantic.dark.text[k];
      }
    } else if (group === 'border') {
      for (const k of ['default', 'subtle', 'strong'] as const) {
        out[`semantic.light.border.${k}`] = theme.semantic.light.border[k];
        out[`semantic.dark.border.${k}`] = theme.semantic.dark.border[k];
      }
    }
  }

  out['opacityBases.primaryStep'] = String(theme.opacityBases.primaryStep);
  out['opacityBases.secondaryStep'] = String(theme.opacityBases.secondaryStep);
  out['opacityBases.neutralDarkStep'] = String(theme.opacityBases.neutralDarkStep);
  out['opacityBases.neutralLightStep'] = String(theme.opacityBases.neutralLightStep);

  const glassKeys = ['fill', 'fillElevated', 'stroke', 'highlight', 'shade', 'blur', 'saturate'] as const;
  for (const k of glassKeys) {
    out[`glass.light.${k}`] = theme.glass.light[k];
    out[`glass.dark.${k}`] = theme.glass.dark[k];
  }

  const rk = ['xs', 'sm', 'md', 'lg', 'xl', 'full'] as const;
  for (const k of rk) {
    out[`ui.radius.${k}`] = theme.ui.radius[k];
  }
  const mk = [
    'durationXs',
    'durationSm',
    'durationMd',
    'durationLg',
    'durationXl',
    'easingStandard',
    'easingEmphasized',
    'easingDecelerate',
  ] as const;
  for (const k of mk) {
    out[`ui.motion.${k}`] = theme.ui.motion[k];
  }
  out['ui.control.paddingX'] = theme.ui.control.paddingX;
  out['ui.control.paddingY'] = theme.ui.control.paddingY;
  out['ui.control.minHeightTouch'] = theme.ui.control.minHeightTouch;
  out['ui.control.borderWidth'] = theme.ui.control.borderWidth;

  const bpk = [
    'watchMax',
    'xs',
    'sm',
    'md',
    'lg',
    'xl',
    'twoXl',
    'threeXl',
    'fourXl',
  ] as const;
  for (const k of bpk) {
    out[`ui.breakpoints.${k}`] = theme.ui.breakpoints[k];
  }

  return out;
}

/** Narrow helper: typed steps for opacity bases. */
function asStep(n: number): PaletteStep {
  const allowed = new Set<number>(PALETTE_STEPS);
  if (!allowed.has(n)) return 50;
  return n as PaletteStep;
}

/**
 * Rebuild a `DesignTheme` from a flat map (e.g. API). Unknown keys ignored; missing keys fall back to `defaults`.
 */
export function unflattenTheme(flat: FlatThemeRecord, defaults: DesignTheme): DesignTheme {
  const get = (key: string, fallback: string) => flat[key] ?? fallback;

  const next: DesignTheme = JSON.parse(JSON.stringify(defaults)) as DesignTheme;

  for (const family of PRIMITIVE_FAMILIES) {
    for (const step of PALETTE_STEPS) {
      const lk = `primitives.${family}.light.${step}`;
      const dk = `primitives.${family}.dark.${step}`;
      if (flat[lk]) next.primitives[family].light[step] = get(lk, next.primitives[family].light[step]);
      if (flat[dk]) next.primitives[family].dark[step] = get(dk, next.primitives[family].dark[step]);
    }
  }

  const stateKeys = [
    'hover',
    'pressed',
    'focus',
    'focusBorder',
    'disabled',
    'selected',
    'hoverOverlay',
    'pressedOverlay',
    'disabledText',
    'disabledBg',
    'focusRing',
  ] as const;
  for (const k of stateKeys) {
    if (flat[`state.light.${k}`]) next.state.light[k] = get(`state.light.${k}`, next.state.light[k]);
    if (flat[`state.dark.${k}`]) next.state.dark[k] = get(`state.dark.${k}`, next.state.dark[k]);
  }

  for (const mode of ['light', 'dark'] as const) {
    for (const k of ['primary', 'secondary', 'tertiary', 'inverse'] as const) {
      const key = `semantic.${mode}.bg.${k}`;
      if (flat[key]) next.semantic[mode].bg[k] = get(key, next.semantic[mode].bg[k]);
    }
    for (const k of ['primary', 'secondary', 'disabled', 'inverse'] as const) {
      const key = `semantic.${mode}.text.${k}`;
      if (flat[key]) next.semantic[mode].text[k] = get(key, next.semantic[mode].text[k]);
    }
    for (const k of ['default', 'subtle', 'strong'] as const) {
      const key = `semantic.${mode}.border.${k}`;
      if (flat[key]) next.semantic[mode].border[k] = get(key, next.semantic[mode].border[k]);
    }
    const ik = `semantic.${mode}.icon.primary`;
    if (flat[ik]) next.semantic[mode].icon.primary = get(ik, next.semantic[mode].icon.primary);
  }

  if (flat['opacityBases.primaryStep'])
    next.opacityBases.primaryStep = asStep(Number(flat['opacityBases.primaryStep']));
  if (flat['opacityBases.secondaryStep'])
    next.opacityBases.secondaryStep = asStep(Number(flat['opacityBases.secondaryStep']));
  if (flat['opacityBases.neutralDarkStep'])
    next.opacityBases.neutralDarkStep = asStep(Number(flat['opacityBases.neutralDarkStep']));
  if (flat['opacityBases.neutralLightStep'])
    next.opacityBases.neutralLightStep = asStep(Number(flat['opacityBases.neutralLightStep']));

  const glassKeys = ['fill', 'fillElevated', 'stroke', 'highlight', 'shade', 'blur', 'saturate'] as const;
  for (const mode of ['light', 'dark'] as const) {
    for (const k of glassKeys) {
      const key = `glass.${mode}.${k}`;
      if (flat[key])
        (next.glass[mode] as unknown as Record<string, string>)[k] = get(key, next.glass[mode][k]);
    }
  }

  const rk = ['xs', 'sm', 'md', 'lg', 'xl', 'full'] as const;
  for (const k of rk) {
    const key = `ui.radius.${k}`;
    if (flat[key]) next.ui.radius[k] = get(key, next.ui.radius[k]);
  }
  const mk = [
    'durationXs',
    'durationSm',
    'durationMd',
    'durationLg',
    'durationXl',
    'easingStandard',
    'easingEmphasized',
    'easingDecelerate',
  ] as const;
  for (const k of mk) {
    const key = `ui.motion.${k}`;
    if (flat[key]) next.ui.motion[k] = get(key, next.ui.motion[k]);
  }
  if (flat['ui.control.paddingX']) next.ui.control.paddingX = get('ui.control.paddingX', next.ui.control.paddingX);
  if (flat['ui.control.paddingY']) next.ui.control.paddingY = get('ui.control.paddingY', next.ui.control.paddingY);
  if (flat['ui.control.minHeightTouch'])
    next.ui.control.minHeightTouch = get('ui.control.minHeightTouch', next.ui.control.minHeightTouch);
  if (flat['ui.control.borderWidth']) next.ui.control.borderWidth = get('ui.control.borderWidth', next.ui.control.borderWidth);

  const bpk = [
    'watchMax',
    'xs',
    'sm',
    'md',
    'lg',
    'xl',
    'twoXl',
    'threeXl',
    'fourXl',
  ] as const;
  for (const k of bpk) {
    const key = `ui.breakpoints.${k}`;
    if (flat[key]) next.ui.breakpoints[k] = get(key, next.ui.breakpoints[k]);
  }

  return next;
}
